import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminAuthStore } from "@/stores/adminAuthStore";
import { useAdminOAuth2 } from "@/hooks/useAdminOAuth2";
import { adminOAuth2Service, isOAuthProviderVisible } from "@/lib/oauth2";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

interface OAuth2Provider {
  name: string;
  displayName: string;
}

const PROVIDER_ICONS: Record<string, string> = {
  wiaccount: "Wi",
  "localhost-oauth": "Tp",
};

export const LoginPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAdminAuthStore();
  const { initiateOAuth } = useAdminOAuth2();
  const [providers, setProviders] = useState<OAuth2Provider[]>([]);
  const [providersLoading, setProvidersLoading] = useState(true);
  const [loadingProvider, setLoadingProvider] = useState<string | null>(null);

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard");
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    adminOAuth2Service
      .getProviders()
      .then((data) =>
        setProviders(data.filter((p) => isOAuthProviderVisible(p.name))),
      )
      .catch((err) => console.error("Failed to load OAuth2 providers:", err))
      .finally(() => setProvidersLoading(false));
  }, []);

  const handleOAuthLogin = async (providerName: string) => {
    setLoadingProvider(providerName);
    const callbackUrl = `${window.location.origin}/auth/oauth/callback`;
    await initiateOAuth(providerName, callbackUrl);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/40">
      <div className="w-full max-w-md px-4">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold tracking-tight">
            WiServices Admin
          </h1>
          <p className="text-muted-foreground mt-2">
            Admin Portal Management System
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Sign In</CardTitle>
            <CardDescription>
              Sign in with your account to access the admin portal
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {providersLoading ? (
              <div className="flex justify-center py-4">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            ) : providers.length === 0 ? (
              <p className="text-sm text-destructive-ink text-center">
                No login providers available
              </p>
            ) : (
              providers.map((provider) => {
                const isThisLoading = loadingProvider === provider.name;
                const anyLoading = loadingProvider !== null;
                const icon =
                  PROVIDER_ICONS[provider.name] ||
                  provider.displayName.charAt(0).toUpperCase();

                return (
                  <Button
                    key={provider.name}
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => handleOAuthLogin(provider.name)}
                    disabled={anyLoading}
                  >
                    <span className="mr-2 inline-flex items-center justify-center w-5 h-5 rounded bg-primary text-primary-foreground font-bold text-xs">
                      {icon}
                    </span>
                    {isThisLoading
                      ? "Redirecting..."
                      : `Sign in with ${provider.displayName}`}
                  </Button>
                );
              })
            )}
          </CardContent>
          <CardFooter className="flex flex-col space-y-2">
            <div className="text-sm text-muted-foreground text-center">
              WiServices Admin Portal v1.0
            </div>
          </CardFooter>
        </Card>

        <p className="text-center text-sm text-muted-foreground mt-8">
          By signing in, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
};
